/* ═══════════════════════════════════════════════════════════════
   Time & Expense rebuild (Tranche 4) — the shell.

   "Time & Expense" nav item, three tabs: Timesheets / Expenses /
   Vacation. Forecasting is NOT here — it moved to its own top-level
   nav item (D2, Tranche 3). Tabs stay mounted once visited (hidden
   via CSS, not unmounted) so switching away and back never loses
   in-progress state — the contract this tranche is required to meet.
   Unvisited tabs are never mounted, so opening this page still only
   pays for the Timesheets load, same as before.
   ═══════════════════════════════════════════════════════════════ */

import React, { useEffect, useState } from 'react';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import TimesheetsPage from './TimesheetsPage';
import { ExpensesTab } from './ExpensesTab';
import { VacationTab } from './VacationTab';
import type { AuthUser } from '../../services/authService';
import type { TimeExpenseDeepLink } from './types';

export interface TimeExpensePageProps {
  toast: (msg: string, isError?: boolean) => void;
  authUser?: AuthUser | null;
  deepLink?: TimeExpenseDeepLink | null;
  onDeepLinkConsumed?: () => void;
  /** Project Canvas R1 investigation: additive only, no behavior change for
   *  today's sidebar caller. tab/weekStart/memberId/projectId are already
   *  honored (via this shell + useTimesheetData); this flag just lets a
   *  future project host nest the page without wiring the app-shell's
   *  one-shot deep-link consumption. TimesheetsPage.tsx and
   *  useTimesheetData.ts are untouched — out of scope here. */
  embedded?: boolean;
}

type ShellTab = 'timesheets' | 'expenses' | 'vacation';

// The deep-link shape's `tab` field still types 'forecasting' (old value,
// kept in navigation.ts for back-compat with anything that might still
// reference it) — nothing live emits it anymore (Forecasting is its own
// view now). Any unrecognized/unset tab falls back to Timesheets, exactly
// like the old shell's default.
function normalizeTab(t?: TimeExpenseDeepLink['tab']): ShellTab {
  if (t === 'expenses' || t === 'vacation') return t;
  return 'timesheets';
}

export default function TimeExpensePage({
  toast,
  authUser,
  deepLink,
  onDeepLinkConsumed,
  embedded,
}: TimeExpensePageProps) {
  const [tab, setTab] = useState<ShellTab>(() => normalizeTab(deepLink?.tab));
  const [visited, setVisited] = useState<Set<ShellTab>>(
    () => new Set([normalizeTab(deepLink?.tab)])
  );
  // TimesheetsPage/ExpensesTab/VacationTab all type authUser as
  // `AuthUser | undefined` (never null) — app.tsx's state is
  // `AuthUser | null`. Normalize once here rather than widening three
  // already-verified prop types.
  const user = authUser ?? undefined;

  function visit(t: ShellTab) {
    setVisited((prev) => (prev.has(t) ? prev : new Set(prev).add(t)));
  }

  useEffect(() => {
    if (!deepLink) return;
    const target = normalizeTab(deepLink.tab);
    setTab(target);
    visit(target);
    // Timesheets consumes projectId/weekStart/memberId itself (via
    // useTimesheetData) and calls onDeepLinkConsumed when it does — see
    // the forwarded `deepLink` prop below. Neither Expenses nor Vacation
    // ever accepted a pre-filter (same as the pre-rebuild page), so for
    // those two the tab switch IS the whole deep link and we consume it
    // here.
    if (target !== 'timesheets' && !embedded) onDeepLinkConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deepLink]);

  function selectTab(next: string) {
    const t = next as ShellTab;
    setTab(t);
    visit(t);
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <div style={{ padding: '0 var(--space-6)', flexShrink: 0 }}>
        <Tabs value={tab} onValueChange={selectTab}>
          <TabsList>
            <TabsTrigger value="timesheets">Timesheets</TabsTrigger>
            <TabsTrigger value="expenses">Expenses</TabsTrigger>
            <TabsTrigger value="vacation">Vacation</TabsTrigger>
          </TabsList>
        </Tabs>
      </div>

      <div style={{ flex: 1, overflow: 'hidden' }}>
        {visited.has('timesheets') && (
          <div style={{ display: tab === 'timesheets' ? 'block' : 'none', height: '100%' }}>
            <TimesheetsPage
              toast={toast}
              authUser={user}
              deepLink={tab === 'timesheets' ? (deepLink ?? null) : null}
              onDeepLinkConsumed={embedded ? undefined : onDeepLinkConsumed}
            />
          </div>
        )}
        {visited.has('expenses') && (
          <div
            style={{
              display: tab === 'expenses' ? 'block' : 'none',
              height: '100%',
              overflow: 'auto',
            }}
          >
            <ExpensesTab toast={toast} authUser={user} />
          </div>
        )}
        {visited.has('vacation') && (
          <div
            style={{
              display: tab === 'vacation' ? 'block' : 'none',
              height: '100%',
              overflow: 'auto',
            }}
          >
            <VacationTab toast={toast} authUser={user} />
          </div>
        )}
      </div>
    </div>
  );
}
